import { Emoji } from "emoji-picker-react";
import ReactDOMServer from "react-dom/server";
import moment from "moment";
import { AxiosError } from "axios";
import { message } from "antd";

export const convertCustomMarkdownToHTML = (text, htmlContent = null) => {
  if (htmlContent?.includes("<google-sheets-html-origin>")) {
    return htmlContent;
  }

  const isOnlyEmojis = text
    .trim()
    .split(/\s+/)
    .every((word) => /^:\w+:$/.test(word));
  const emojiSize = isOnlyEmojis ? 32 : 21; // Set size based on condition

  text = sanitizeCode(text);

  // Bold: Convert **text** to <span class="renderer_bold">text</span>
  text = text.replace(/\*\*(.*?)\*\*/g, (match, content) => {
    // Check if the content between ** is empty or contains only whitespace
    if (content.trim() === "") {
      return match; // Return the original match if empty or whitespace
    }
    return `<span class="renderer_bold">${content}</span>`;
  });

  // Italic: Convert *text* to <span class="renderer_italic">text</span>
  text = text.replace(/\*(.*?)\*/g, (match, content) => {
    // Check if the content between * is empty or contains only whitespace
    if (content.trim() === "") {
      return match; // Return the original match if empty or whitespace
    }
    return `<span class="renderer_italic">${content}</span>`;
  });

  // Strikethrough: Convert ~~text~~ to <span class="renderer_strikethrough">text</span>
  text = text.replace(/~~(.*?)~~/g, (match, content) => {
    // Check if the content between ~~ is empty or contains only whitespace
    if (content.trim() === "") {
      return match; // Return the original match if empty or whitespace
    }
    return `<span class="renderer_strikethrough">${content}</span>`;
  });

  // Heading (e.g., ### heading): Convert ### text to <h3 class="renderer_heading">text</h3>
  text = text.replace(/^### (.*?)$/gm, (match, content) => {
    // Check if the content after ### is empty or contains only whitespace
    if (content.trim() === "") {
      return match; // Return the original match if empty or whitespace
    }
    return `<h3 class="renderer_heading">${content}</h3>`;
  });

  // Quote: Convert > text to <blockquote class="renderer_quote">text</blockquote>
  text = text.replace(/^> (.*?)$/gm, (match, content) => {
    // Check if the content after > is empty or contains only whitespace
    if (content.trim() === "") {
      return match; // Return the original match if empty or whitespace
    }
    return `<blockquote class="renderer_quote">${content}</blockquote>`;
  });

  // Code Block: Convert ```code``` to <code class="renderer_code">code</code>
  text = text.replace(/```(.*?)```/gs, (match, code) => {
    // Check if the content inside ``` is empty or contains only whitespace
    if (code.trim() === "") {
      return match; // Return the original match if empty or whitespace
    }
    return `<code class="renderer_code">${code}</code>`;
  });

  // Plain Link: Convert plain URLs to <a href="url" class="renderer_link">url</a>
  text = text.replace(
    /(^|\s)(https?:\/\/[^\s<>]+)/g,
    (match, space, url) =>
      `${space}<a href="${url}" target="_blank" class="renderer_link">${url}</a>`
  );

  // Link: Convert [text](url) to <a href="url" class="renderer_link">text</a>
  text = text.replace(/\[(.*?)\]\((.*?)\)/g, (match, linkText, url) => {
    // Check if linkText or url is empty or contains only whitespace
    if (linkText.trim() === "" || url.trim() === "") {
      return match; // Return the original match if invalid
    }

    // Regular expression for basic URL validation
    const isValidUrl = /^(https?:\/\/|mailto:|ftp:\/\/|file:\/\/|tel:).+/i.test(
      url
    );

    // Use the fallback URL if the current URL is invalid
    const finalUrl = isValidUrl ? url : `http://${url}/`;

    return `<a href="${finalUrl}" class="renderer_link" target="_blank">${linkText}</a>`;
  });

  // New Link Format 1: Convert (url) to <a href="url" class="renderer_link">url</a>
  text = text.replace(
    /\((https?:\/\/[^\s\[\]]+)\)/g,
    (match, url) =>
      `(<a href="${url}" class="renderer_link" target="_blank">${url}</a>)`
  );

  // New Link Format 2: Convert [url] to [<a href="url" class="renderer_link" target="_blank">url</a>]
  text = text.replace(
    /\[(https?:\/\/[^\s\[\]]+)\]/g,
    (match, url) =>
      `[<a href="${url}" class="renderer_link" target="_blank">${url}</a>]`
  );

  // Bulleted List: Convert lines starting with "- " to <ul><li>...</li></ul>
  text = text.replace(
    /^- (.*?)(\n|$)/gm,
    '<ul class="renderer_bulleted"><li>$1</li></ul>'
  );

  // Numbered List: Convert lines starting with "1. " to <ol><li>...</li></ol>
  text = text.replace(
    /(^\d+\.)\s(.*?)(\n|$)/gm,
    (match, number, content) => `<li>${content}</li>`
  );

  // Wrap the result in a single <ol> tag for the numbered list
  text = text
    .replace(/<\/li>\s*<li>/g, "</li><li>")
    .replace(/^<li>/, '<ol class="renderer_numbered"><li>')
    .replace(/<\/li>$/, "</li></ol>");

  // Emoji: Convert :1fae0: to <span><Emoji unified="1fae0"/></span>
  text = text.replace(/:([a-zA-Z0-9-]+):/g, (match, unified) => {
    const emojiElement = <Emoji unified={unified} size={emojiSize} />;
    const rawData = ReactDOMServer.renderToStaticMarkup(emojiElement);
    return convertRawImgToSpan(rawData, unified, `${emojiSize}px`);
  });

  // Convert %% text %% to HTMl content
  if (htmlContent) {
    text = text.replace(/%%([\s\S]*?)%%/gm, (match, content) => {
      return htmlContent;
    });
  }

  // Remove leading/trailing newlines
  text = text.replace(/^\n+|\n+$/g, "");

  // New Line: If spaces than convert in code tag
  text = text.replace(/^\s+.*$/, "<code>$&</code>");

  // New Line: Convert remaining newlines to <br> tags
  // text = text.replace(/\n/g, "<br>");

  return text;
};

export const convertHTMLToCustomMarkdown = (html) => {
  if (html) {
    // Convert bold: <span class="renderer_bold">text</span> to **text**
    html = html.replace(/<span class="renderer_bold">(.*?)<\/span>/g, "**$1**");

    // Convert italic: <span class="renderer_italic">text</span> to *text*
    html = html.replace(/<span class="renderer_italic">(.*?)<\/span>/g, "*$1*");

    // Convert strikethrough: <span class="renderer_strikethrough">text</span> to ~~text~~
    html = html.replace(
      /<span class="renderer_strikethrough">(.*?)<\/span>/g,
      "~~$1~~"
    );

    // Convert heading: <h3 class="renderer_heading">text</h3> to ### text
    html = html.replace(/<h3 class="renderer_heading">(.*?)<\/h3>/g, "### $1");

    // Convert quote: <blockquote class="renderer_quote">text</blockquote> to > text
    html = html.replace(
      /<blockquote class="renderer_quote">(.*?)<\/blockquote>/g,
      "> $1"
    );

    // Convert code block: <code class="renderer_code">code</code> to ```code```
    html = html.replace(
      /<code class="renderer_code">(.*?)<\/code>/gs,
      "```$1```"
    );

    // Conversion for (url) format
    html = html.replace(
      /\(<a href="(https?:\/\/[^\s\[\]]+)" class="renderer_link" target="_blank">\1<\/a>\)/g,
      "($1)"
    );

    // Conversion for [url] format
    html = html.replace(
      /\[<a href="(https?:\/\/[^\s\[\]]+)" class="renderer_link" target="_blank">\1<\/a>\]/g,
      "[$1]"
    );

    // Conversion for[text](url) format
    html = html.replace(
      /<a href="([^"]+)" class="renderer_link" target="_blank">(.*?)<\/a>/g,
      (match, url, linkText) => `[${linkText}](${url})`
    );

    // Plain Link: Convert plain URLs to <a href="url" class="renderer_link">url</a>
    html = html.replace(
      /<a href="(https?:\/\/[^\s<>]+)" target="_blank" class="renderer_link">\1<\/a>/g,
      "$1"
    );

    // Convert mentions: <span class="renderer_mention">@username</span> to @username
    html = html.replace(
      /<span class="renderer_mention">@([a-zA-Z0-9_]+)<\/span>/g,
      "@$1"
    );

    // Convert bulleted list: <ul class="renderer_bulleted"><li>text</li></ul> to - text
    html = html.replace(
      /<ul class="renderer_bulleted"><li>(.*?)<\/li><\/ul>/gs,
      "- $1"
    );

    // Convert numbered list: <ol class="renderer_numbered"><li>text</li></ol> to 1. text
    html = html.replace(
      /<ol class="renderer_numbered"><li>(.*?)<\/li><\/ol>/gs,
      "1. $1"
    );

    // // Convert emojis: <span alt=":emoji:" class="renderer_emoji" ...></span> to :emoji:
    html = html.replace(
      /<span[^>]*id="([^"]+)"[^>]*class="renderer_emoji"[^>]*>.*?<\/span>/g,
      "$1"
    );

    // Convert new lines: <br> to \n
    html = html.replace(/<br>/g, "\n");

    // Remove any remaining HTML tags (to ensure plain text output)
    html = html.replace(/<\/?[^>]+(>|$)/g, "");
  }
  return unsanitizeText(html);
};

export const getPageNumberForMessageId = (
  chalListData,
  targetMessageId,
  messagesPerPage = 30
) => {
  let totalMessagesProcessed = 0;

  // Iterate through each date group in chalListData
  for (let dateGroup of chalListData) {
    // Calculate how many messages are in this date group
    const messagesInGroup = dateGroup.messages.length;

    // Check if the target message is in this group
    const messageIndex = dateGroup.messages.findIndex(
      (msg) => msg._id === targetMessageId
    );

    if (messageIndex !== -1) {
      // Calculate the page number
      const absoluteMessagePosition = totalMessagesProcessed + messageIndex + 1;
      return Math.ceil(absoluteMessagePosition / messagesPerPage);
    }

    // Add the number of messages in this group to total processed
    totalMessagesProcessed += messagesInGroup;
  }

  // If message not found, return null or throw an error
  return null;
};

export function deleteMessageFromChatlist(chalListData, messageId) {
  // Create a deep copy of chalListData to avoid direct mutation
  const updatedChalListData = JSON.parse(JSON.stringify(chalListData));

  // Iterate through each date group
  for (let i = 0; i < updatedChalListData.length; i++) {
    const dateGroup = updatedChalListData[i];

    // Find the index of the message to be deleted
    const messageIndex = dateGroup.messages.findIndex(
      (msg) => msg._id === messageId
    );

    // If message is found
    if (messageIndex !== -1) {
      // Remove the message from the messages array
      dateGroup.messages.splice(messageIndex, 1);

      // Decrease the count of messages
      dateGroup.count -= 1;

      // If no messages left, remove the entire date group
      if (dateGroup.messages.length === 0) {
        updatedChalListData.splice(i, 1);
      }

      // Return the updated chalListData
      return updatedChalListData;
    }
  }

  // If message not found, return the original data
  return chalListData;
}

export function messageSeen(
  chalListData,
  messageId,
  isChannel,
  currentUser,
  oppositeUser
) {
  // Deep copy chalListData to avoid direct mutation
  const updatedChalListData = JSON.parse(JSON.stringify(chalListData));

  if (!isChannel) {
    // Iterate through the messages in the chalListData
    updatedChalListData.forEach((chat) => {
      chat.messages.forEach((message) => {
        // Update is_seen only if the message _id matches messageId
        if (
          message._id === messageId &&
          message.is_seen === false &&
          message.receiverId === currentUser?._id
        ) {
          message.is_seen = true;
        }

        // Check replies within the matched message
        if (
          message._id === messageId &&
          message.replies &&
          message.replies.length > 0
        ) {
          message.replies.forEach((reply) => {
            if (
              reply.is_seen === false &&
              reply.receiverId === currentUser?._id
            ) {
              reply.is_seen = true;
            }
          });
        }
      });
    });
  } else {
    // Handle the case when isChannel is true
    updatedChalListData.forEach((chat) => {
      chat.messages.forEach((message) => {
        // Add currentUser._id to readBy if message _id matches messageId
        if (
          message._id === messageId &&
          !message.readBy.includes(currentUser?._id)
        ) {
          message.readBy.push(currentUser?._id);
        }

        // Check replies within the matched message
        if (
          message._id === messageId &&
          message.replies &&
          message.replies.length > 0
        ) {
          message.replies.forEach((reply) => {
            if (!reply.readBy.includes(currentUser?._id)) {
              reply.readBy.push(currentUser?._id);
            }
          });
        }
      });
    });
  }

  return updatedChalListData;
}

export const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : "?");

export function findPageNumber(data, messageId, pageSize = 30) {
  console.warn(messageId, "messageIdmessageId");
  // Flatten all messages across reversed days into a single array
  const allMessages = data.flatMap((day) =>
    day.messages.map((message) => ({ dayId: day._id, message }))
  );

  // Reverse the order of days without mutating the original array
  const reversedData = [...allMessages].reverse();

  // Find the index of the message with the given _id
  const messageIndex = reversedData.findIndex(
    (entry) => entry.message._id == messageId
  );

  if (messageIndex === -1) {
    return "Message not found";
  }

  // Calculate the page number (1-based index)
  const pageNumber = Math.floor(messageIndex / pageSize) + 1;
  console.warn(pageNumber, "messageIdmessageId");

  return pageNumber;
}
export function sanitizeCode(text) {
  return text
    .split("\n")
    .map((line) =>
      line.trim().startsWith(">")
        ? line
        : line
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#x27;")
    )
    .join("\n");
}

export function unsanitizeText(sanitizedText) {
  return sanitizedText
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");
}

export function convertRawImgToSpan(rawdata, unified, Size = "32px") {
  if (!rawdata) {
    return `:${unified}:`;
  }
  // Use a single regex to extract both src and alt
  const match = rawdata.match(/src="([^"]+)"[^>]*alt="([^"]+)"/);

  if (!match) {
    throw new Error("Invalid rawdata: Missing src or alt attributes.");
  }

  const src = match[1]; // Extracted src value
  const alt = match[2]; // Extracted alt value

  // Construct the <span> string directly
  return `<span alt=":${alt}:" id=":${unified}:" class="renderer_emoji" title=":${alt}:" style="background-image: url('${src}');width: ${Size};height: ${Size}"></span>`;
}

export function populateChatUserData(
  sortedDateGroups,
  currentUser,
  targetUser
) {
  return sortedDateGroups.map((group) => {
    const updatedMessages = group.messages.map((message) => {
      // Skip processing if receiverInfo and senderInfo already exist
      if (message.receiverInfo && message.senderInfo) {
        return message;
      }

      const updatedMessage = { ...message };

      // Add receiver information if not already present
      if (!updatedMessage.receiverInfo) {
        if (updatedMessage.receiverId === currentUser._id) {
          updatedMessage.receiverInfo = [{ ...currentUser }];
        } else if (updatedMessage.receiverId === targetUser._id) {
          updatedMessage.receiverInfo = [{ ...targetUser }];
        }
      }

      // Add sender information if not already present
      if (!updatedMessage.senderInfo) {
        if (updatedMessage.senderId === targetUser._id) {
          updatedMessage.senderInfo = { ...targetUser };
        } else if (updatedMessage.senderId === currentUser._id) {
          updatedMessage.senderInfo = { ...currentUser };
        }
      }

      return updatedMessage;
    });

    return { ...group, messages: updatedMessages };
  });
}

export const scrollToBottomByID = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollTop = element.scrollHeight;
  }
};

export const getIsChannel = () => {
  const userData = localStorage.getItem("current_userdata");
  if (!userData) return true; // Default to true if no data

  try {
    const { username } = JSON.parse(userData) || {};
    return !username;
  } catch (error) {
    console.error("Error parsing user data:", error);
    return true; // Assume true in case of error
  }
};

export const addMessageLocally = (
  messageList,
  setMessageList,
  newMessageData,
  currentUser,
  recipientUser
) => {
  // Guard clause for invalid input
  if (!messageList || !Array.isArray(messageList)) return;

  // Get today's date as ID
  const todayId = moment().format("YYYY-MM-DD");

  // Check if today's date entry exists
  const todayEntryExists = messageList.some((entry) => entry._id === todayId);

  // Create a new array to avoid direct mutation
  let updatedMessageList;

  if (todayEntryExists) {
    // Update existing entry for today
    updatedMessageList = messageList.map((entry) =>
      entry._id === todayId
        ? {
            ...entry,
            messages: [
              // Mark all existing messages as seen
              ...entry.messages.map((msg) => ({ ...msg, is_seen: true })),
              // Add new message
              newMessageData,
            ],
            count: entry.count + 1,
          }
        : entry
    );
  } else {
    // Create new entry for today
    updatedMessageList = [
      ...messageList,
      {
        _id: todayId,
        messages: [newMessageData],
        count: 1,
      },
    ];
  }

  // Process data to include user information
  const processedMessageList = populateChatUserData(
    updatedMessageList,
    currentUser,
    recipientUser
  );

  // Update state with processed data
  setMessageList(processedMessageList);
};

export const is24HoursOld = (timestamp) => {
  const givenTime = moment(timestamp);
  const twentyFourHoursAgo = moment().subtract(24, "hours");
  return givenTime.isBefore(twentyFourHoursAgo);
};

export const handleApiError = (error, customMessage) => {
  // Check if it's a canceled request error
  if (error instanceof AxiosError && error.code === "ERR_CANCELED") {
    // Silently ignore canceled requests
    return;
  }

  if (error instanceof AxiosError) {
    if (error.response?.data?.message) {
      console.error(error.response.data.message);
      // message.error(error.response.data.message)
    } else if (error.request) {
      // No response received (Network error, server down, CORS issues)
      console.error("No response from the server. Please try again later.");
    } else {
      // Other Axios errors (bad request, client-side issues)
      console.error(
        customMessage || "Something went wrong while processing your request."
      );
    }
  } else if (error instanceof Error) {
    // Standard JavaScript errors (TypeError, ReferenceError, etc.)
    console.error(error.message || "An unexpected error occurred.");
  } else {
    // Unknown error (rare cases)
    console.error(customMessage || "An unknown error occurred.");
  }
};

export const localStorageUtil = {
  setItem(key, value) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      // If it's already a string, store directly; otherwise serialize it.
      const toStore = typeof value === "string" ? value : JSON.stringify(value);
      localStorage.setItem(key, toStore);
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  },

  getItem(key, defaultValue = null) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return defaultValue;
      }

      // Simple heuristic: if it starts with { or [ it's probably JSON
      const firstChar = stored.trim()[0];
      if (firstChar === "{" || firstChar === "[") {
        try {
          return JSON.parse(stored);
        } catch {
          // not valid JSON after all
        }
      }
      return stored;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return defaultValue;
    }
  },

  removeItem(key) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from localStorage:", error);
    }
  },

  clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  },

  keyExists(key) {
    try {
      if (!key || typeof key !== "string") {
        throw new Error("Storage key must be a non-empty string.");
      }
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error("Error checking existence in localStorage:", error);
      return false;
    }
  },
};

export const countEmojiReactions = (data) => {
  return Object.values(
    data.reduce((acc, { emoji, username, userId, _id }) => {
      const extractedUsername = username || (userId && userId.username) || null;
      if (!acc[emoji]) {
        acc[emoji] = { emoji, username: [], count: 0, _id: [] };
      }
      acc[emoji].username.push(extractedUsername);
      acc[emoji]._id.push(_id);
      acc[emoji].count += 1;
      return acc;
    }, {})
  );
};

export const mergeMessages = (newMessages, existingMessages) => {
  // Pre-process date parsing for sorting optimization
  const parseDate = (dateStr) => {
    // Use timestamp for faster comparisons
    return new Date(dateStr).getTime();
  };

  // Create indexed structure for existing data (day groups and messages)
  const dayGroups = Object.create(null); // Faster than regular object or Map for known string keys
  const messagesByDay = Object.create(null);

  // Process old data first (single pass)
  for (let i = 0; i < newMessages.length; i++) {
    const day = newMessages[i];
    dayGroups[day._id] = {
      _id: day._id,
      messages: [], // Placeholder - will populate after message processing
      count: 0, // Will calculate after merging
      parsedDate: parseDate(day._id), // Pre-parse date for sorting
    };

    // Create ultra-fast lookup for messages
    messagesByDay[day._id] = Object.create(null);

    // Process all messages in this day
    for (let j = 0; j < day.messages.length; j++) {
      const msg = day.messages[j];
      messagesByDay[day._id][msg._id] = {
        ...msg,
        parsedDate: parseDate(msg.createdAt), // Pre-parse date for sorting
      };
    }
  }

  // Process new data (single pass)
  for (let i = 0; i < existingMessages.length; i++) {
    const newDay = existingMessages[i];

    // If day doesn't exist yet, create it
    if (!dayGroups[newDay._id]) {
      dayGroups[newDay._id] = {
        _id: newDay._id,
        messages: [], // Placeholder
        count: 0, // Will calculate later
        parsedDate: parseDate(newDay._id),
      };
      messagesByDay[newDay._id] = Object.create(null);
    }

    // Process all new messages
    for (let j = 0; j < newDay.messages.length; j++) {
      const newMsg = newDay.messages[j];

      // Update or add message
      if (messagesByDay[newDay._id][newMsg._id]) {
        // Update existing message (merge properties)
        Object.assign(messagesByDay[newDay._id][newMsg._id], newMsg);
        // Make sure parsed date is updated if createdAt changed
        messagesByDay[newDay._id][newMsg._id].parsedDate = parseDate(
          newMsg.createdAt
        );
      } else {
        // Add new message
        messagesByDay[newDay._id][newMsg._id] = {
          ...newMsg,
          parsedDate: parseDate(newMsg.createdAt),
        };
      }
    }
  }

  // Final assembly and sorting (single pass)
  const result = [];

  // Get all day IDs
  const dayIds = Object.keys(dayGroups);

  // For each day, assemble final messages array
  for (let i = 0; i < dayIds.length; i++) {
    const dayId = dayIds[i];
    const dayMsgs = Object.values(messagesByDay[dayId]);

    // Sort messages by creation time using pre-parsed timestamps (much faster than parsing in comparator)
    dayMsgs.sort((a, b) => a.parsedDate - b.parsedDate);

    // Cleanup: remove parsed date helper property from final output
    for (let j = 0; j < dayMsgs.length; j++) {
      delete dayMsgs[j].parsedDate;
    }

    // Assemble day group
    dayGroups[dayId].messages = dayMsgs;
    dayGroups[dayId].count = dayMsgs.length;

    // Add to result array
    result.push(dayGroups[dayId]);
  }

  // Sort day groups by date using pre-parsed timestamps
  result.sort((a, b) => a.parsedDate - b.parsedDate);

  // Cleanup: remove parsed date helper property from final output
  for (let i = 0; i < result.length; i++) {
    delete result[i].parsedDate;
  }

  return result;
};

export const getMentionedUserIds = (message, users) => {
  const mentionedUsernames = [...message.matchAll(/@(\w+)/g)].map(
    (match) => match[1]
  );

  return mentionedUsernames
    .map((username) => {
      const user = users.find((user) => user.userName === username);
      return user ? user._id : null;
    })
    .filter(Boolean);
};

export const extractFileName = (filePath) => {
  const normalizedPath = filePath.replace(/\\/g, "/");
  const fullFileName = normalizedPath.split("/").pop();

  const extension = fullFileName.split(".").pop();
  const baseName = fullFileName.slice(0, -(extension.length + 1));

  // Truncate the base name to 15 characters
  const truncatedName =
    baseName.length > 15 ? baseName.slice(0, 15) + "..." : baseName;

  return `${truncatedName}.${extension}`;
};

export const removePinMessage = (pinMessageId, data) => {
  if (!pinMessageId || typeof pinMessageId !== "string") {
    throw new Error("Pin message ID must be a non-empty string");
  }

  if (!data || typeof data !== "object" || !Array.isArray(data.pinMessages)) {
    throw new Error("Data must be an object with a pinMessages array");
  }

  const updatedData = { ...data };

  const messageIndex = data.pinMessages.findIndex(
    (msg) => msg._id === pinMessageId
  );

  if (messageIndex === -1) {
    return updatedData;
  }

  updatedData.pinMessages = [
    ...data.pinMessages.slice(0, messageIndex),
    ...data.pinMessages.slice(messageIndex + 1),
  ];

  updatedData.pinnedMessageCount = updatedData.pinMessages.length;

  return updatedData;
};


export const removeByIdFromObjectArray1D = (items, targetId) => {
  return items.filter((item) => item._id !== targetId);
};
export const requestNotificationPermission = () => {
  if ("Notification" in window && Notification.permission !== "granted") {
    Notification.requestPermission();
  }
};